import React from "react";
import { Icon } from "../UIElements/Icon";
import { Switch } from "../UIElements/Switch";
import { useToast } from "@finitless/shared/hooks";
import { PROMO_STATUS } from "./discountsConstants";

const PromotionCard = ({
  promotion,
  onEdit,
  onDelete,
  onToggleStatus,
  isUpdating,
}) => {
  const toast = useToast();
  if (!promotion) {
    return null;
  }

  const isActive = promotion.status === PROMO_STATUS.ACTIVE;
  const isExpired = promotion.status === PROMO_STATUS.EXPIRED;
  const isDisabled = promotion.status === PROMO_STATUS.DISABLED;
  const apiEnabled = promotion.raw?.enabled;
  const hasCode = Boolean(promotion.coupon && String(promotion.coupon).trim());
  const displayCode = hasCode ? promotion.coupon : "No code";
  const getStatusBadgeClasses = () => {
    if (isActive) return "bg-green-500/10 text-green-400 border-green-500/30";
    if (isExpired) return "bg-red-500/10 text-red-400 border-red-500/30";
    if (isDisabled) return "bg-gray-700/60 text-gray-400 border-gray-600";
    return "bg-blue-500/10 text-blue-400 border-blue-500/30";
  };

  const handleCardClick = () => {
    if (isUpdating) {
      return;
    }
    onEdit(promotion);
  };

  const handleActionClick = (event, action) => {
    event.stopPropagation();
    if (isUpdating) {
      return;
    }
    action();
  };

  return (
    <div
      onClick={handleCardClick}
      tabIndex={0}
      role="button"
      aria-label={`Open promotion ${promotion.name}`}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleCardClick();
        }
      }}
      className={`relative cursor-pointer overflow-hidden rounded-xl border border-gray-800 bg-gray-900 transition-all duration-300 select-none hover:border-blue-500/40 hover:shadow-xl focus:ring-2 focus:ring-blue-500 focus:outline-none ${
        isUpdating ? "pointer-events-none cursor-wait opacity-60" : ""
      }`}
    >
      <div className="relative h-40 overflow-hidden">
        {promotion.image ? (
          <img
            src={promotion.image}
            alt={promotion.name}
            className={`h-full w-full object-cover transition-transform duration-500 ${
              isDisabled ? "grayscale" : "hover:scale-105"
            }`}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-800 text-sm text-gray-400">
            No image
          </div>
        )}
        <div className="absolute top-3 right-3">
          <span
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold backdrop-blur-md ${getStatusBadgeClasses()}`}
          >
            {promotion.status}
          </span>
        </div>
        <div className="absolute right-0 bottom-0 left-0 h-20 bg-gradient-to-t from-gray-950 to-transparent" />
      </div>

      <div className="p-5">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3
              className={`mb-1 line-clamp-2 min-h-[2.75rem] text-lg leading-tight font-semibold break-words ${
                isDisabled ? "text-gray-400" : "text-white"
              }`}
            >
              {promotion.name}
            </h3>
            <p className="flex items-center gap-1 text-xs text-gray-400">
              <Icon name="Calendar" className="h-3 w-3" />
              {promotion.start} - {promotion.end}
            </p>
          </div>
        </div>

        <div className="mt-1 h-5">
          <p className="truncate text-sm text-gray-400">
            {promotion.description}
          </p>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-lg border border-gray-800 bg-gray-950/60 p-3">
          <div className="text-left">
            <p className="text-xs font-medium text-gray-500 uppercase">
              {promotion.raw?.type === 2 ? "Code" : "Automatic"}
            </p>
            {promotion.raw?.type === 2 && (
              <>
                <div className="flex items-center gap-2">
                  <code
                    className={`font-mono font-semibold ${
                      !hasCode
                        ? "text-gray-600"
                        : isDisabled
                          ? "text-gray-500"
                          : "text-blue-400"
                    }`}
                  >
                    {displayCode}
                  </code>
                  {hasCode && (
                    <button
                      type="button"
                      onClick={(event) =>
                        handleActionClick(event, () =>
                          navigator.clipboard
                            .writeText(promotion.coupon || "")
                            .then(() =>
                              toast.success("Coupon code copied to clipboard"),
                            ),
                        )
                      }
                      className="text-gray-500 transition-colors hover:text-white"
                      aria-label="Copy discount code"
                    >
                      <Icon name="Copy" className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="text-right">
            <p className="text-xs font-medium text-gray-500 uppercase">
              Discount
            </p>
            <p
              className={`font-semibold ${
                isDisabled ? "text-gray-400" : "text-white"
              }`}
            >
              {promotion.rate_type === 1
                ? `${promotion.rate}%`
                : `$${promotion.rate}`}{" "}
              OFF
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between border-t border-gray-800 pt-4">
          <div className="flex items-center gap-2">
            <div
              onClick={(event) => event.stopPropagation()}
              className="flex items-center"
            >
              <Switch
                checked={!!apiEnabled}
                onCheckedChange={() => onToggleStatus(promotion.id)}
                disabled={isUpdating}
              />
            </div>
          </div>

          <div
            className="flex items-center gap-2"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={(event) =>
                handleActionClick(event, () => onEdit(promotion))
              }
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-700 hover:text-blue-400"
              aria-label="Edit promotion"
              title="Edit promotion"
            >
              <Icon name="Edit" className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={(event) =>
                handleActionClick(event, () => onDelete(promotion.id))
              }
              disabled={isUpdating}
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-700 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Delete promotion"
              title="Delete promotion"
            >
              <Icon name="Trash" className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionCard;
